import { useEffect, useState } from "react";
import { apiFetch } from "../../../api";

/** `GET /admin/onsite/connectors/{connectorId}/workspace`: the onsite workspace a File Drop calls. */
type ConnectorWorkspace =
  | { status: "unique"; gcpProjectId: string; region: string | null }
  | { status: "none" }
  | { status: "several" };

type WorkspaceLookup = ConnectorWorkspace | { status: "loading" } | { status: "error" };

/** A read-only line about the SFTP wrapper; `warning` means the runner has no wrapper to call. */
interface WrapperSourceLine {
  text: string;
  warning: boolean;
}

/** The onsite workspace whose SFTP wrapper a File Drop calls, as the platform resolves it. */
export function useConnectorWorkspace(connectorId: string | undefined): WorkspaceLookup {
  const [lookup, setLookup] = useState<WorkspaceLookup>({ status: "loading" });

  useEffect(() => {
    if (!connectorId) return;
    let cancelled = false;
    apiFetch<ConnectorWorkspace>(
      `/api/admin/onsite/connectors/${encodeURIComponent(connectorId)}/workspace`,
    )
      .then((answer) => {
        if (!cancelled) setLookup(answer);
      })
      .catch(() => {
        if (!cancelled) setLookup({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [connectorId]);

  return lookup;
}

const NO_UNIQUE_WORKSPACE = {
  none: "This project has no onsite workspace, so the SFTP wrapper's GCP project is unknown. Set up the onsite workspace, or set an SFTP Wrapper URL.",
  several:
    "This project has more than one onsite workspace, so which wrapper to use is ambiguous. Disable all but one of this project's onsite workspaces, or set an SFTP Wrapper URL.",
} as const;

/** What the form says about the SFTP wrapper; the runner's default region is us-central1. */
export function wrapperSourceLine(
  lookup: WorkspaceLookup,
  sftpWrapperUrl: string,
): WrapperSourceLine | null {
  if (sftpWrapperUrl.trim()) {
    return {
      text: "The SFTP Wrapper URL below is used instead of the onsite workspace's wrapper.",
      warning: false,
    };
  }
  switch (lookup.status) {
    case "loading":
      return null;
    case "error":
      return { text: "Could not check this project's onsite workspace.", warning: false };
    case "unique":
      return {
        text: `SFTP wrapper: ${lookup.gcpProjectId} (${lookup.region ?? "us-central1"}), from the onsite workspace.`,
        warning: false,
      };
    case "none":
    case "several":
      return { text: NO_UNIQUE_WORKSPACE[lookup.status], warning: true };
  }
}
